import React, { useState } from 'react'
import styled from 'styled-components'
import copy from 'copy-to-clipboard'
import { useRouteMatch } from 'react-router-dom'
import { useToast } from '@sketch/toasts'
import {
  Box,
  Input,
  TextArea,
  Checkbox,
  Form,
  PasswordInput,
  generatePassword,
  Flex,
  FlatButton,
} from '@sketch/components'
import { routes } from '@sketch/modules-common'
import { usePreviewForm } from './usePreviewForm'
import { PreviewLinkSection } from './PreviewLinkSection'
import { DatePicker } from '../../../activity/components/Notifications/NotificationSearch/NotificationSearch.styles'
import { DocumentInfoHeader } from './DocumentInfoHeader'

const DatePickerStyled = styled(DatePicker)`
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: ${({ theme }) => theme.fontSizes.E};
`

const PasswordToggle = styled(FlatButton)`
  padding: 0 12px;
  margin-top: 1px; /* stylelint-disable-line scales/space */
  height: 22px;
  font-size: ${({ theme }) => theme.fontSizes.D};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  text-decoration: underline;
`

const Header = styled.p`
  /* has to be important because paragraphs have a default padding bottom of 20  */
  padding-bottom: 24px !important; /* stylelint-disable-line declaration-no-important */
`

const OptionsHeader = styled.p`
  /* has to be important because paragraphs have a default padding bottom of 20  */
  padding-bottom: 8px !important; /* stylelint-disable-line declaration-no-important */
`

interface SettingsTabProps {
  shareName: string
  shareThumbnail?: string
  shareIdentifier: string
  isEditMode: boolean
}

export const SettingsTab = ({
  shareName,
  shareThumbnail,
  shareIdentifier,
  isEditMode,
}: SettingsTabProps) => {
  const [showPasswordInput, setShowPasswordInput] = useState(!isEditMode)
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    initialValues,
  } = usePreviewForm()
  const { showToast } = useToast()

  const handleOnCopy = () => {
    copy(values.password)
    showToast('Password copied')
  }

  const handleOnGeneratePassword = () => {
    setFieldValue('password', generatePassword(12))
  }

  const isInWorkspaceSettings = useRouteMatch(
    routes.WORKSPACE_SETTINGS_DOCUMENT_PREVIEWS.template()
  )

  return (
    <>
      {isInWorkspaceSettings && (
        <DocumentInfoHeader
          name={shareName}
          identifier={shareIdentifier}
          thumbnail={shareThumbnail}
        />
      )}
      {isEditMode ? (
        <PreviewLinkSection />
      ) : (
        <Header>
          Create a preview to share only specific pages and prototypes of{' '}
          <strong>&quot;{shareName}&quot;</strong>.
        </Header>
      )}
      <Box height={359}>
        <Form.Field
          name="previewName"
          label="Preview Name"
          errorText={touched.previewName ? errors.previewName : undefined}
        >
          <Input
            type="text"
            name="previewName"
            placeholder="Give this preview a name..."
            value={values.previewName}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Form.Field>

        <Form.Field name="description" label="Description (Optional)">
          <TextArea
            name="description"
            placeholder="This preview is for..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={255}
          />
        </Form.Field>
        <Box mb={2}>
          <OptionsHeader>Preview Options</OptionsHeader>
          <Box mb={2}>
            <Checkbox
              name="inspectEnabled"
              checked={values.inspectEnabled}
              onChange={handleChange}
            >
              Allow inspecting and asset downloads
            </Checkbox>
          </Box>
          <Box mb={2}>
            <Checkbox
              name="commentsEnabled"
              checked={values.commentsEnabled}
              onChange={handleChange}
            >
              Allow commenting with Sketch account
            </Checkbox>
          </Box>
          <Box mb={2}>
            <Flex alignItems="center">
              <Checkbox
                name="passwordProtected"
                checked={values.passwordProtected}
                onChange={e => {
                  if (!isEditMode) {
                    handleChange(e)
                    return
                  }

                  // when the preview previously wasn't password protected
                  if (!initialValues.passwordProtected) {
                    setShowPasswordInput(!showPasswordInput)
                    handleChange(e)
                    return
                  }

                  if (showPasswordInput) {
                    setShowPasswordInput(false)
                    setFieldValue('password', '')
                    setFieldTouched('password', false)
                  }

                  handleChange(e)
                }}
              >
                Protect with password
              </Checkbox>
              {values.passwordProtected && !showPasswordInput && (
                <PasswordToggle onClick={() => setShowPasswordInput(true)}>
                  Create New Password
                </PasswordToggle>
              )}
            </Flex>
            {showPasswordInput && values.passwordProtected && (
              <Box mt={1} ml={7}>
                <Form.Field
                  name="password"
                  errorText={touched.password ? errors.password : undefined}
                >
                  <PasswordInput
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onPwdCopy={handleOnCopy}
                    onGeneratePassword={handleOnGeneratePassword}
                  />
                </Form.Field>
              </Box>
            )}
          </Box>
          <Box mb={2}>
            <Checkbox
              name="expiryDateEnabled"
              checked={values.expiryDateEnabled}
              onChange={handleChange}
            >
              Expire preview link on…
            </Checkbox>
            {values.expiryDateEnabled && (
              <Box mt={1} ml={7} width={150}>
                <DatePickerStyled
                  name="expiryDate"
                  type="date"
                  placeholder="Expire on"
                  aria-label="Expire on"
                  value={values.expiryDate}
                  isDarkMode={false}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}
